import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { BehaviorSubject } from 'rxjs';
import { DealerState, UpdateDealerFlagsAction } from '../state/DealerState';
import { FeatureflagsService } from './featureflags.service';
import { FEATURE_FLAGS } from '../constants/constants';
import { NOTIFICATION_RESOURCE_TYPES } from '../models/notification-resource-type.model';

/**
 * Backoffice is delivered for NAFA CAM Spec notifications. Shopfloor is out of
 * scope for this release, but the storage remains future-compatible with a
 * shared feature-flag map so a mobile/shopfloor channel can be added without
 * changing the current dealer persistence model.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationPreferencesService {
  private readonly store = inject(Store);
  private readonly featureFlagsService = inject(FeatureflagsService);
  private readonly notificationsFeatureEnabledSubject = new BehaviorSubject<boolean>(true);
  private readonly enabledResourceTypesSubject = new BehaviorSubject<readonly string[]>(
    NOTIFICATION_RESOURCE_TYPES.map((definition) => definition.resourceType)
  );

  public readonly notificationsFeatureEnabled$ = this.notificationsFeatureEnabledSubject.asObservable();
  public readonly enabledResourceTypes$ = this.enabledResourceTypesSubject.asObservable();

  constructor() {
    this.store.select(DealerState.featureFlags).subscribe((featureFlags) => {
      const flags = featureFlags?.flags;
      this.notificationsFeatureEnabledSubject.next(this.resolveNotificationsEnabled(flags));
      this.enabledResourceTypesSubject.next(
        NOTIFICATION_RESOURCE_TYPES.filter((definition) => this.resolveFlag(flags, definition.preferenceFlag)).map(
          (definition) => definition.resourceType
        )
      );
    });
  }

  public get isFeatureEnabled(): boolean {
    return this.notificationsFeatureEnabledSubject.value;
  }

  public isResourceTypeEnabled(resourceType: string): boolean {
    return this.enabledResourceTypesSubject.value.includes(resourceType);
  }

  public async setResourceTypeEnabled(resourceType: string, enabled: boolean): Promise<boolean> {
    const definition = NOTIFICATION_RESOURCE_TYPES.find((candidate) => candidate.resourceType === resourceType);
    return definition ? this.setFlag(definition.preferenceFlag, enabled) : false;
  }

  private async setFlag(flagKey: FEATURE_FLAGS, enabled: boolean): Promise<boolean> {
    const dealerId = this.getCurrentDealerId();
    if (!dealerId) {
      return false;
    }

    const currentFlags = (this.store.selectSnapshot(DealerState.featureFlags)?.flags as unknown as { [key: string]: boolean }) ?? {};
    const merged = { ...currentFlags, [flagKey]: enabled };
    delete merged[FEATURE_FLAGS.LEGACY_NAFA_CAM_SPEC_NOTIFICATIONS_ENABLED];

    try {
      const saved = await this.featureFlagsService.saveFeatureFlags(dealerId, merged);
      this.store.dispatch(new UpdateDealerFlagsAction(saved));
      return true;
    } catch {
      return false;
    }
  }

  private getCurrentDealerId(): string | undefined {
    try {
      return this.store.selectSnapshot(DealerState.currentDealer)?.id;
    } catch {
      return undefined;
    }
  }

  private resolveFlag(flags: unknown, flagKey: FEATURE_FLAGS): boolean {
    const flagsRecord = (flags as { [key: string]: boolean }) ?? {};
    const value = flagsRecord[flagKey];
    return typeof value === 'boolean' ? value : true;
  }

  private resolveNotificationsEnabled(flags: unknown): boolean {
    const flagsRecord = (flags as { [key: string]: boolean }) ?? {};
    const currentValue = flagsRecord[FEATURE_FLAGS.NOTIFICATIONS_ENABLED];
    if (typeof currentValue === 'boolean') {
      return currentValue;
    }
    return this.resolveFlag(flagsRecord, FEATURE_FLAGS.LEGACY_NAFA_CAM_SPEC_NOTIFICATIONS_ENABLED);
  }
}
